import {
    Alert,
    Autocomplete,
    Box,
    CircularProgress,
    Grid,
    SxProps,
    TextField,
    Theme,
    Typography,
} from '@mui/material'
import { Dispatch, Fragment, SetStateAction, useEffect, useState } from 'react'
import API from '@enums/api'

/*Drop down box that is populated by the results of searching of all users of an organisation*/

export type UserDropDownOption = {
    label: string
    id: string
}

type Props = {
    options: UserDropDownOption[]
    setOptions: Dispatch<SetStateAction<UserDropDownOption[]>>
    selectedUser: string | undefined
    setSelectedUser: Dispatch<SetStateAction<string | undefined>>
    setShowForm?: Dispatch<SetStateAction<boolean>>
    selectedOrg: string
    sx?: SxProps<Theme>
    required?: boolean
}

const SelectUsers = (props: Props) => {
    const {
        options,
        setOptions,
        setShowForm,
        setSelectedUser,
        selectedOrg,
        sx,
        required,
    } = props

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        setLoading(true)
        const userFetch = async () => {
            const res = await API.searches.listOrgUsers(selectedOrg)

            if (!res.ok) {
                console.error(res.error)
                setError('Failed to fetch users')
                return
            }

            setOptions(
                res.value.map((user) => ({
                    label: `${user.first_name} ${user.last_name} (${user.role}) `,
                    id: user.id.toString(),
                }))
            )
        }
        userFetch()
        setLoading(false)
    }, [])

    const [open, setOpen] = useState(false)

    if (error) {
        setTimeout(() => {
            setOpen(false)
            setOptions([])
            setError(null)
        }, 2000)
        return (
            <div>
                <Alert severity="error">
                    <Grid container>
                        <Grid item xs={2}>
                            <CircularProgress
                                variant="indeterminate"
                                disableShrink
                                color="primary"
                                size={20}
                                sx={{
                                    animationDuration: '2000ms',
                                    marginRight: 4,
                                }}
                            />
                        </Grid>
                        <Grid item xs={10}>
                            <Typography variant="body1">{error}</Typography>
                        </Grid>
                    </Grid>
                </Alert>
            </div>
        )
    }

    return (
        <>
            <Box
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                }}
                sx={sx}
            >
                <Autocomplete
                    style={{ width: '100%' }}
                    disablePortal
                    disabled={loading}
                    loading={loading}
                    open={open}
                    onOpen={() => {
                        setOpen(true)
                    }}
                    onClose={() => {
                        setOpen(false)
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            required={required}
                            label="User"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <Fragment>
                                        {loading ? (
                                            <CircularProgress
                                                color="primary"
                                                size={20}
                                            />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </Fragment>
                                ),
                            }}
                        />
                    )}
                    options={options}
                    defaultValue={options[0]}
                    onChange={(_e, value) => {
                        if (setShowForm) setShowForm(true)
                        setSelectedUser(value?.id)
                    }}
                />
            </Box>
        </>
    )
}

export default SelectUsers
